/**
 * Modal behaviour for the phone's bottom sheets.
 *
 * `role="dialog" aria-modal="true"` tells a screen reader to stay inside the
 * sheet. It tells the TAB KEY nothing at all — without a trap, the fourth tab
 * out of the swap sheet lands on the chore list behind it, which is still
 * clickable, still tappable, and completely invisible to someone driving the
 * screen from a keyboard. So the three things a modal actually owes its user
 * live here rather than being re-typed, slightly differently, in each sheet:
 *
 *  1. **Focus moves in** on open, to the first thing worth answering.
 *  2. **Focus stays in** — Tab from the last control wraps to the first,
 *     Shift+Tab from the first wraps to the last.
 *  3. **Focus goes back** to whatever opened the sheet when it closes, so the
 *     Swap button you pressed is still under your finger afterwards.
 *
 * Escape closes, and stops there rather than bubbling into whatever else on
 * the page happens to listen for it.
 *
 * It lives under `chores/` for the same reason `live.tsx` does: `phone/` itself
 * belongs to another owner, and `proposals/` imports it from here.
 */

import { useCallback, useEffect, useRef } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react'

/**
 * Everything focusable, in DOM order. `tabindex="-1"` is deliberately excluded:
 * it is reachable by script but not by Tab, so it is not part of the cycle.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    el => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true',
  )
}

/**
 * A radio group is ONE tab stop, not one per radio: Tab enters at the checked
 * radio and leaves the group entirely. Collapsing each group to its checked
 * member (or its first, when nothing is checked) is what makes the wrap land
 * where a browser would put it rather than on the last radio in the list.
 */
function tabStops(root: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = []
  const seenRadioGroups = new Set<string>()
  for (const el of focusableWithin(root)) {
    if (el instanceof HTMLInputElement && el.type === 'radio' && el.name !== '') {
      if (seenRadioGroups.has(el.name)) continue
      seenRadioGroups.add(el.name)
      const group = [
        ...root.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${el.name}"]`),
      ]
      out.push(group.find(r => r.checked) ?? group[0] ?? el)
      continue
    }
    out.push(el)
  }
  return out
}

export interface DialogHandles<T extends HTMLElement, I extends HTMLElement> {
  /** Put this on the dialog element itself. */
  containerRef: RefObject<T | null>
  /** Put this on the control that should hold focus when the sheet opens. */
  initialRef: RefObject<I | null>
  /** Wire to `onKeyDown` on the dialog: Escape closes, Tab wraps. */
  onKeyDown(e: ReactKeyboardEvent): void
}

/**
 * Wire up a modal sheet. `onClose` must be stable (a `useCallback`) or the
 * handler is rebuilt on every render.
 */
export function useDialogFocus<T extends HTMLElement, I extends HTMLElement = HTMLElement>(
  onClose: () => void,
): DialogHandles<T, I> {
  const containerRef = useRef<T | null>(null)
  const initialRef = useRef<I | null>(null)
  const returnToRef = useRef<Element | null>(null)

  // Captured during render setup, before the effect below steals focus, so the
  // element we hand focus back to is the one that opened the sheet.
  if (returnToRef.current === null && typeof document !== 'undefined') {
    returnToRef.current = document.activeElement
  }

  useEffect(() => {
    const target = initialRef.current ?? tabStops(containerRef.current ?? document.body)[0] ?? null
    target?.focus()

    const returnTo = returnToRef.current
    return () => {
      // Only take focus back if the sheet still holds it; if something else has
      // legitimately claimed focus meanwhile, stealing it would be worse.
      const active = document.activeElement
      const inside = containerRef.current?.contains(active ?? null) ?? false
      if ((inside || active === document.body || active === null) && returnTo instanceof HTMLElement) {
        returnTo.focus()
      }
    }
  }, [])

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const root = containerRef.current
      if (root === null) return
      const stops = tabStops(root)
      if (stops.length === 0) return
      const first = stops[0]!
      const last = stops[stops.length - 1]!
      const active = document.activeElement
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (active === last || !root.contains(active))) {
        e.preventDefault()
        first.focus()
      }
    },
    [onClose],
  )

  return { containerRef, initialRef, onKeyDown }
}
